import type {
  Product,
  Pagination,
  CollectionDto,
  CollectionPack,
  CollectionProduct,
  CollectionFilters,
  FacetValue,
  CollectionPageResponse,
  ProductPageResponse,
  PackPageResponse,
  PackSummaryResponse,
  PricingContext,
  Review,
  ReviewStats,
  ReviewFilters,
  CreateReviewInput,
  QuoteItem,
  QuoteResult,
  BatchResolveItem,
  BatchResolveResult,
  ResolveProductPriceResult,
  CreditsConfig,
  CreditsBalance,
  CreditsTransactions,
  CustomerCoupons,
  Cart,
  CreateCartInput,
  UpdateCartInput,
  AddPackToCartInput,
  AuthResponse,
  UserProfile,
  LoginRequest,
  RegisterRequest,
  GoogleAuthRequest,
  ShippingAddress,
  CreateShippingAddressInput,
  UpdateShippingAddressInput,
  Order,
  OrderPagination,
  RefundOrderRequest,
  CustomerSubscription,
  SubscriptionItemUpdate,
} from './types';

export interface StorefrontClientOptions {
  baseUrl: string;
  tenantId: string;
  headers?: Record<string, string>;
}

function requestHeaders(
  opts: StorefrontClientOptions,
  body: unknown,
): Record<string, string> {
  const headers: Record<string, string> = { ...(opts.headers ?? {}) };

  // Public API Gateway CORS derives tenant from the host and rejects X-Tenant-ID
  // on browser preflights. Keep the header map limited to CORS-allowed values.
  delete headers['X-Tenant-ID'];
  delete headers['x-tenant-id'];

  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }

  return headers;
}

async function request<T>(
  opts: StorefrontClientOptions,
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const url = `${opts.baseUrl}${path}`;
  const res = await fetch(url, {
    method,
    headers: requestHeaders(opts, body),
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`BFF ${method} ${path} failed: ${res.status} ${text}`);
  }

  return res.json() as Promise<T>;
}

/** Same as request() but always includes cookies (auth domain). */
async function credentialedRequest<T>(
  opts: StorefrontClientOptions,
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const url = `${opts.baseUrl}${path}`;
  const res = await fetch(url, {
    method,
    credentials: 'include',
    headers: requestHeaders(opts, body),
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`BFF ${method} ${path} failed: ${res.status} ${text}`);
  }

  // 204 / logout-style no-content
  if (res.status === 204) return undefined as unknown as T;

  return res.json() as Promise<T>;
}

function qs(params: Record<string, string | number | boolean | string[] | undefined>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined) continue;
    if (Array.isArray(v)) {
      for (const item of v) sp.append(k, item);
    } else {
      sp.set(k, String(v));
    }
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

function pricingContextParams(ctx: PricingContext): Record<string, string | undefined> {
  return { ...ctx };
}

function reviewFilterParams(filters: ReviewFilters): Record<string, string | number | boolean | undefined> {
  return { ...filters };
}

export interface StorefrontClient {
  // Products
  listProducts(query?: { q?: string; page?: number; limit?: number } & PricingContext): Promise<{
    data: Product[];
    meta: { pagination: Pagination };
  }>;
  getProduct(id: string, ctx?: PricingContext): Promise<{ data: Product }>;
  getRecommendations(
    productId: string,
    query?: { limit?: number } & PricingContext,
  ): Promise<{ data: Product[] }>;

  // Collections
  listCollections(query?: { page?: number; limit?: number }): Promise<{
    data: CollectionDto[];
    meta: { pagination: Pagination };
  }>;
  getMenuCollections(): Promise<{ data: CollectionDto[] }>;
  getCollection(
    slug: string,
    filters?: CollectionFilters,
  ): Promise<{
    data: {
      collection: CollectionDto;
      products: CollectionProduct[];
      packs?: CollectionPack[];
      facets: Record<string, FacetValue[]>;
    };
    meta: { pagination: Pagination };
  }>;
  searchProducts(query?: { q?: string; page?: number; limit?: number }): Promise<{
    data: {
      collection: CollectionDto;
      products: CollectionProduct[];
      facets: Record<string, FacetValue[]>;
    };
    meta: { pagination: Pagination };
  }>;

  // Page bundles
  getCollectionPage(slug: string, filters?: CollectionFilters): Promise<CollectionPageResponse>;
  getProductPage(slug: string, ctx?: PricingContext): Promise<ProductPageResponse>;
  getPackPage(slug: string, ctx?: PricingContext): Promise<PackPageResponse>;
  getPackById(id: string, ctx?: PricingContext): Promise<PackSummaryResponse>;

  // Reviews
  listReviews(
    productId: string,
    filters?: ReviewFilters,
  ): Promise<{
    data: Review[];
    meta: {
      total: number;
      limit: number;
      offset: number;
      hasMore: boolean;
      stats?: ReviewStats;
    };
  }>;
  getReviewStats(productId: string): Promise<{ data: ReviewStats }>;
  createReview(input: CreateReviewInput): Promise<{ data: Review }>;
  voteReview(
    reviewId: string,
    voteType: 'helpful' | 'not_helpful',
  ): Promise<{ data: Review }>;

  // Carts
  getCart(token: string): Promise<{ data: Cart }>;
  createCart(input: CreateCartInput): Promise<{ data: Cart }>;
  updateCart(token: string, input: UpdateCartInput): Promise<{ data: Cart }>;
  addPackToCart(token: string, input: AddPackToCartInput): Promise<{ data: Cart }>;

  // Pricing
  getFreeShippingThreshold(currency?: string): Promise<{
    data: { threshold: number | null; currency: string };
  }>;
  quote(items: QuoteItem[], context: PricingContext & { currency: string }): Promise<{ data: QuoteResult }>;
  resolvePricesBatch(
    items: BatchResolveItem[],
    context: PricingContext & { currency: string },
  ): Promise<{ data: BatchResolveResult }>;
  resolveProductPrice(query: {
    product_id: string;
    variant_id?: string;
    quantity?: number;
  } & PricingContext &
    { currency: string }): Promise<{ data: ResolveProductPriceResult }>;
  getCreditsConfig(currency?: string): Promise<{ data: CreditsConfig }>;
  getCreditsBalance(userId: string): Promise<{ data: CreditsBalance }>;
  getCreditsTransactions(userId: string): Promise<{ data: CreditsTransactions }>;
  getUserCoupons(userId: string): Promise<{ data: CustomerCoupons }>;

  // Auth — cookies managed by BFF (HttpOnly access_token)
  login(input: LoginRequest): Promise<AuthResponse>;
  register(input: RegisterRequest): Promise<AuthResponse>;
  loginWithGoogle(input: GoogleAuthRequest): Promise<AuthResponse>;
  refresh(): Promise<AuthResponse>;
  logout(): Promise<void>;
  getMe(): Promise<{ data: UserProfile }>;

  // Users — shipping addresses (credentialed; requires active BFF session cookie)
  getShippingAddresses(): Promise<{ data: ShippingAddress[] }>;
  getShippingAddress(id: string): Promise<{ data: ShippingAddress }>;
  createShippingAddress(input: CreateShippingAddressInput): Promise<{ data: ShippingAddress }>;
  updateShippingAddress(id: string, input: UpdateShippingAddressInput): Promise<{ data: ShippingAddress }>;
  deleteShippingAddress(id: string): Promise<void>;

  // Orders
  listOrders(query?: {
    user_id?: string;
    email?: string;
    page?: number;
    limit?: number;
  }): Promise<{ data: Order[]; meta: { pagination: OrderPagination } }>;
  getOrder(orderId: string): Promise<{ data: Order }>;
  refundOrder(orderId: string, body: RefundOrderRequest): Promise<{ data: unknown }>;

  // Subscriptions (credentialed; requires active BFF session cookie)
  getCustomerSubscriptions(customerId: string): Promise<{ data: CustomerSubscription[] }>;
  cancelSubscription(customerId: string, subscriptionId: string, body: { reason: string }): Promise<void>;
  pauseSubscription(customerId: string, subscriptionId: string, body?: { reason?: string }): Promise<void>;
  resumeSubscription(customerId: string, subscriptionId: string): Promise<void>;
  skipSubscription(customerId: string, subscriptionId: string, body?: { reason?: string }): Promise<void>;
  rescheduleSubscription(customerId: string, subscriptionId: string, body: { new_next_charge_date: string }): Promise<void>;
  updateSubscriptionItems(customerId: string, subscriptionId: string, body: { items: SubscriptionItemUpdate[] }): Promise<{ data: CustomerSubscription }>;
}

export function createStorefrontClient(opts: StorefrontClientOptions): StorefrontClient {
  const r = <T>(method: string, path: string, body?: unknown) =>
    request<T>(opts, method, path, body);
  const cr = <T>(method: string, path: string, body?: unknown) =>
    credentialedRequest<T>(opts, method, path, body);

  return {
    // ---- Products ----
    listProducts(query = {}) {
      const { currency, country, ...rest } = query;
      const params: Record<string, string | number | boolean | string[] | undefined> = {
        ...rest,
        ...pricingContextParams({ currency, country }),
      };
      return r('GET', `/v1/storefront/products${qs(params)}`);
    },
    getProduct(id, ctx = {}) {
      return r('GET', `/v1/storefront/products/${encodeURIComponent(id)}${qs(pricingContextParams(ctx))}`);
    },
    getRecommendations(productId, query = {}) {
      const { currency, country, ...rest } = query;
      const params: Record<string, string | number | boolean | string[] | undefined> = {
        ...rest,
        ...pricingContextParams({ currency, country }),
      };
      return r(
        'GET',
        `/v1/storefront/products/${encodeURIComponent(productId)}/recommendations${qs(params)}`,
      );
    },

    // ---- Collections ----
    listCollections(query = {}) {
      return r('GET', `/v1/storefront/collections${qs(query)}`);
    },
    getMenuCollections() {
      return r('GET', '/v1/storefront/collections/menu');
    },
    getCollection(slug, filters = {}) {
      const { options, ...rest } = filters;
      const params: Record<string, string | number | boolean | string[] | undefined> = { ...rest };
      if (options) {
        for (const [k, v] of Object.entries(options)) {
          params[`option[${k}]`] = v;
        }
      }
      return r('GET', `/v1/storefront/collections/${encodeURIComponent(slug)}${qs(params)}`);
    },
    searchProducts(query = {}) {
      return r('GET', `/v1/storefront/collections/search${qs(query)}`);
    },

    // ---- Page bundles ----
    getCollectionPage(slug, filters = {}) {
      const { options, ...rest } = filters;
      const params: Record<string, string | number | boolean | string[] | undefined> = { ...rest };
      if (options) {
        for (const [k, v] of Object.entries(options)) {
          params[`option[${k}]`] = v;
        }
      }
      return r('GET', `/v1/storefront/pages/collection/${encodeURIComponent(slug)}${qs(params)}`);
    },
    getProductPage(slug, ctx = {}) {
      return r('GET', `/v1/storefront/pages/product/${encodeURIComponent(slug)}${qs(pricingContextParams(ctx))}`);
    },
    getPackPage(slug, ctx = {}) {
      return r('GET', `/v1/storefront/pages/pack/${encodeURIComponent(slug)}${qs(pricingContextParams(ctx))}`);
    },
    getPackById(id, ctx = {}) {
      return r('GET', `/v1/storefront/packs/${encodeURIComponent(id)}${qs(pricingContextParams(ctx))}`);
    },

    // ---- Reviews ----
    listReviews(productId, filters = {}) {
      return r('GET', `/v1/storefront/products/${encodeURIComponent(productId)}/reviews${qs(reviewFilterParams(filters))}`);
    },
    getReviewStats(productId) {
      return r('GET', `/v1/storefront/products/${encodeURIComponent(productId)}/reviews/stats`);
    },
    createReview(input) {
      return r('POST', '/v1/storefront/reviews', input);
    },
    voteReview(reviewId, voteType) {
      return r('POST', `/v1/storefront/reviews/${encodeURIComponent(reviewId)}/votes`, { voteType });
    },

    // ---- Carts ----
    getCart(token) {
      return r('GET', `/v1/storefront/carts/${encodeURIComponent(token)}`);
    },
    createCart(input) {
      return r('POST', '/v1/storefront/carts', input);
    },
    updateCart(token, input) {
      return r('PUT', `/v1/storefront/carts/${encodeURIComponent(token)}`, input);
    },
    addPackToCart(token, input) {
      return r('POST', `/v1/storefront/carts/${encodeURIComponent(token)}/packs`, input);
    },

    // ---- Pricing ----
    getFreeShippingThreshold(currency = 'USD') {
      return r('GET', `/v1/storefront/pricing/free-shipping-threshold${qs({ currency })}`);
    },
    quote(items, context) {
      return r('POST', '/v1/storefront/pricing/quote', {
        items,
        context: {
          currency: context.currency ?? 'USD',
          country: context.country,
          customer_groups: context.customer_groups?.split(',').map((s) => s.trim()).filter(Boolean),
          channel: context.channel,
        },
      });
    },
    resolvePricesBatch(items, context) {
      return r('POST', '/v1/storefront/pricing/resolve/batch', {
        items,
        context: {
          currency: context.currency ?? 'USD',
          country: context.country,
          customer_groups: context.customer_groups?.split(',').map((s) => s.trim()).filter(Boolean),
          channel: context.channel,
        },
      });
    },
    resolveProductPrice(query) {
      const { product_id, variant_id, quantity, ...ctx } = query;
      return r(
        'GET',
        `/v1/storefront/pricing/product${qs({
          product_id,
          variant_id,
          quantity,
          ...pricingContextParams(ctx),
        })}`,
      );
    },

    // ---- Auth ----
    login(input) {
      return cr('POST', '/v1/storefront/auth/login', input);
    },
    register(input) {
      return cr('POST', '/v1/storefront/auth/register', input);
    },
    loginWithGoogle(input) {
      return cr('POST', '/v1/storefront/auth/google', input);
    },
    refresh() {
      return cr('POST', '/v1/storefront/auth/refresh', {});
    },
    logout() {
      return cr('POST', '/v1/storefront/auth/logout', {});
    },
    getMe() {
      return cr('GET', '/v1/storefront/auth/me');
    },

    // ---- Users ----
    getShippingAddresses() {
      return cr('GET', '/v1/storefront/users/me/shipping-addresses');
    },
    getShippingAddress(id) {
      return cr('GET', `/v1/storefront/users/me/shipping-addresses/${encodeURIComponent(id)}`);
    },
    createShippingAddress(input) {
      return cr('POST', '/v1/storefront/users/me/shipping-addresses', input);
    },
    updateShippingAddress(id, input) {
      return cr('PATCH', `/v1/storefront/users/me/shipping-addresses/${encodeURIComponent(id)}`, input);
    },
    deleteShippingAddress(id) {
      return cr('DELETE', `/v1/storefront/users/me/shipping-addresses/${encodeURIComponent(id)}`);
    },

    // ---- Orders ----
    listOrders(query = {}) {
      return cr('GET', `/v1/storefront/orders${qs(query)}`);
    },
    getOrder(orderId) {
      return cr('GET', `/v1/storefront/orders/${encodeURIComponent(orderId)}`);
    },
    refundOrder(orderId, body) {
      return cr('POST', `/v1/storefront/orders/${encodeURIComponent(orderId)}/refunds`, body);
    },

    // ---- Subscriptions ----
    getCustomerSubscriptions(customerId) {
      return cr('GET', `/v1/storefront/subscriptions/customers/${encodeURIComponent(customerId)}`);
    },
    cancelSubscription(customerId, subscriptionId, body) {
      return cr(
        'POST',
        `/v1/storefront/subscriptions/customers/${encodeURIComponent(customerId)}/subscriptions/${encodeURIComponent(subscriptionId)}/cancel`,
        body
      );
    },
    pauseSubscription(customerId, subscriptionId, body = {}) {
      return cr(
        'POST',
        `/v1/storefront/subscriptions/customers/${encodeURIComponent(customerId)}/subscriptions/${encodeURIComponent(subscriptionId)}/pause`,
        body
      );
    },
    resumeSubscription(customerId, subscriptionId) {
      return cr(
        'POST',
        `/v1/storefront/subscriptions/customers/${encodeURIComponent(customerId)}/subscriptions/${encodeURIComponent(subscriptionId)}/resume`
      );
    },
    skipSubscription(customerId, subscriptionId, body = {}) {
      return cr(
        'POST',
        `/v1/storefront/subscriptions/customers/${encodeURIComponent(customerId)}/subscriptions/${encodeURIComponent(subscriptionId)}/skip`,
        body
      );
    },
    rescheduleSubscription(customerId, subscriptionId, body) {
      return cr(
        'PUT',
        `/v1/storefront/subscriptions/customers/${encodeURIComponent(customerId)}/subscriptions/${encodeURIComponent(subscriptionId)}/reschedule`,
        body
      );
    },
    updateSubscriptionItems(customerId, subscriptionId, body) {
      return cr(
        'PUT',
        `/v1/storefront/subscriptions/customers/${encodeURIComponent(customerId)}/subscriptions/${encodeURIComponent(subscriptionId)}/items`,
        body
      );
    },

    // Credits
    getCreditsConfig(currency: string = 'USD') {
      return cr('GET', `/v1/storefront/credits/config?currency=${encodeURIComponent(currency)}`);
    },
    getCreditsBalance(userId: string) {
      return cr('GET', `/v1/storefront/credits/balance/${encodeURIComponent(userId)}`);
    },
    getCreditsTransactions(userId: string) {
      return cr('GET', `/v1/storefront/credits/transactions/${encodeURIComponent(userId)}`);
    },

    // Coupons
    getUserCoupons(userId: string) {
      return cr('GET', `/v1/storefront/coupons/customers/${encodeURIComponent(userId)}`);
    },
  };
}
